import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Search } from "lucide-react";

const empty = { name: "", contact_person: "", phone: "", email: "", gstin: "", address: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => setItems((await api.get("/suppliers")).data);
  useEffect(() => { load(); }, []);

  const filteredItems = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return items;
    return items.filter((s) => {
      const name = (s.name || "").toString().toLowerCase();
      const contact = (s.contact_person || s.contact || "").toString().toLowerCase();
      const phone = (s.phone || "").toString().toLowerCase();
      return name.includes(term) || contact.includes(term) || phone.includes(term);
    });
  }, [items, searchQuery]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/suppliers", form);
      toast.success("Supplier added");
      setForm(empty);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };
  const remove = async () => {
    if (!deleteTarget) return;
    await api.delete(`/suppliers/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Vendors</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Suppliers</h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="suppliers-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search supplier, contact or phone..."
              className="pl-9 w-64 bg-white"
            />
          </div>
          <Button onClick={() => setOpen(true)} data-testid="add-supplier-btn">
            <Plus size={16} className="mr-1" /> Add Supplier
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>Address</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No suppliers found</TableCell>
              </TableRow>
            )}
            {filteredItems.map((s) => (
              <TableRow key={s.id} className="data-row">
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.contact_person || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.phone || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.gstin || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.address || "—"}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => setDeleteTarget(s)} className="p-1 hover:bg-muted rounded text-destructive">
                    <Trash2 size={14} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Name *</Label><Input data-testid="sup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs">Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs">GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-supplier">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete Supplier</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            Delete <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? This action cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={remove}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
